// src/components/UserProfile.js - With SEO meta tags for social sharing
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { onSnapshot, doc, getDoc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { setPersistence, browserLocalPersistence } from "firebase/auth";
import { auth, db } from "../firebase";
import styles from "./UserProfile.module.css";
import availableIcons from "./icons";
import GhostProfile from "./GhostProfile";
import GhostLinkButton from "./GhostLinkButton";
import UnpublishedProfile from "./UnpublishedProfile";
import { getPaletteColors } from "../utils/templateUtils";
import { Mail, Phone } from 'lucide-react';
import { Helmet } from "react-helmet"; // Import Helmet for SEO

const UserProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [unlockedLinks, setUnlockedLinks] = useState({});
  const [ghostLinkInputs, setGhostLinkInputs] = useState({});
  const [ghostLinkErrors, setGhostLinkErrors] = useState({});
  const [currentTheme, setCurrentTheme] = useState('classic');
  const [clientIp, setClientIp] = useState(null);
  const [ghostSecurityData, setGhostSecurityData] = useState({
    remainingAttempts: 4,
    isLocked: false,
    lockExpiry: null
  });
  const [hasPublished, setHasPublished] = useState(false); // Default to false
  const [userExists, setUserExists] = useState(false); // Add this to track if the user exists

  // Get client IP on mount
  useEffect(() => {
    const getClientIp = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setClientIp(data.ip);
      } catch (err) {
        console.error("Error fetching IP:", err);
        // Fall back to a unique browser fingerprint or timestamp as identifier
        setClientIp(`browser-${Date.now()}`);
      }
    };

    getClientIp();
  }, []);

  // Ensure local persistence
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  }, []);

  // Fetch user by username and attach doc id as uid
  useEffect(() => {
    // First query the usernames collection to get the userId
    const usernameDocRef = doc(db, "usernames", username);
    
    const unsubscribe = onSnapshot(
      usernameDocRef,
      (usernameDoc) => {
        if (!usernameDoc.exists()) {
          console.log("No username found:", username);
          setLoading(false);
          return;
        }

        // User exists in the usernames collection
        setUserExists(true);
        
        // Get the user ID from the username doc
        const userId = usernameDoc.data().userId;
        console.log("Found user ID:", userId);

        // Now fetch data from commit collection using that userId
        const commitDocRef = doc(db, "commit", userId);
        getDoc(commitDocRef)
          .then((commitDoc) => {
            if (commitDoc.exists()) {
              // Use data from commit (published version)
              const data = { ...commitDoc.data(), uid: userId };

              // Check if the profile has been published (has required data)
              if (data.profile && data.profile.lastPublished) {
                console.log("Fetched published user data:", data);
                setUserData(data);
                setHasPublished(true);

                // Set the theme
                const theme = data.profile?.theme || 'classic';
                setCurrentTheme(theme);
                
                // Check ghost mode security if clientIp is available
                if (clientIp && data.profile?.ghostMode?.enabled) {
                  checkGhostAttemptLimits(userId, clientIp);
                }
              } else {
                // User has a commit doc but hasn't fully published
                console.log("User exists but hasn't completed publishing");
                setUserData({ username, uid: userId });
                setHasPublished(false);
              }
              
              setLoading(false);
            } else {
              // If no commit version exists, try to get the user document as fallback
              console.log("No published version found, trying user document");
              return getDoc(doc(db, "users", userId));
            }
          })
          .then((userDoc) => {
            if (!userDoc) return; // Skip if we already found the commit doc
            
            if (userDoc.exists()) {
              // Get data from user document
              const data = { ...userDoc.data(), uid: userId };
              
              // Check if the user has published their profile
              const hasPublishedProfile = 
                data.profile && 
                data.profile.lastPublished &&
                (data.profile.links?.some(link => !link.hidden) || data.profile.bio);
              
              if (hasPublishedProfile) {
                // Use data from user document as fallback
                console.log("Using user document as fallback:", data);
                setUserData(data);
                setHasPublished(true);

                // Set the theme
                const theme = data.profile?.theme || 'classic';
                setCurrentTheme(theme);
                
                // Check ghost mode security if clientIp is available
                if (clientIp && data.profile?.ghostMode?.enabled) {
                  checkGhostAttemptLimits(userId, clientIp);
                }
              } else {
                // User exists but hasn't published profile
                console.log("User exists but hasn't published their profile");
                setUserData({ username, uid: userId });
                setHasPublished(false);
              }
            } else {
              console.log("No user document found for userId:", userId);
              // Still show unpublished profile since the username exists in usernames collection
              setUserData({ username, uid: userId });
              setHasPublished(false);
            }
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching profile data:", error);
            setLoading(false);
          });
      },
      (error) => {
        console.error("Error listening to username document:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [username, navigate, clientIp]);

  // Function to check attempt limits and lockout status
  const checkGhostAttemptLimits = async (userId, ipAddress) => {
    try {
      // Get the user document with ghost mode data
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const ghostData = userData.profile?.ghostMode || {};
      
      // Check if we have failedAttempts tracking - using new array format
      const attempts = ghostData.failedAttempts || {};
      
      // Sanitize IP for Firestore field name (replace dots with underscores)
      const safeIp = ipAddress.replace(/\./g, '_');
      const ipAttempts = attempts[safeIp] || [];
      
      // Configuration
      const MAX_ATTEMPTS = 4;
      const LOCKOUT_DURATION_MINUTES = 15;
      
      // Count recent attempts (within lockout period)
      const now = new Date();
      const lockoutThreshold = new Date(now.getTime() - (LOCKOUT_DURATION_MINUTES * 60000));
      
      // Filter for recent attempts only
      const recentAttempts = ipAttempts.filter(timestamp => {
        // Handle both Firestore Timestamp objects and serialized timestamps
        const attemptDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return attemptDate > lockoutThreshold;
      });
      
      // Calculate if locked and remaining attempts
      const isLocked = recentAttempts.length >= MAX_ATTEMPTS;
      const remainingAttempts = Math.max(0, MAX_ATTEMPTS - recentAttempts.length);
      
      // Calculate when the lockout will expire
      let lockExpiry = null;
      if (isLocked && recentAttempts.length > 0) {
        // Get the oldest attempt that's still counting
        const oldestAttempt = recentAttempts[0];
        const oldestDate = oldestAttempt.toDate ? oldestAttempt.toDate() : new Date(oldestAttempt);
        lockExpiry = new Date(oldestDate.getTime() + (LOCKOUT_DURATION_MINUTES * 60000));
      }
      
      // Update the security data state
      setGhostSecurityData({
        remainingAttempts,
        isLocked,
        lockExpiry: lockExpiry ? lockExpiry.toISOString() : null
      });
      
      console.log("Ghost security data:", {
        remainingAttempts,
        isLocked,
        lockExpiry: lockExpiry ? lockExpiry.toISOString() : null
      });
    } catch (err) {
      console.error("Error checking attempt limits:", err);
    }
  };

  // Add this effect to ensure correct banner height with 3:1 aspect ratio
  useEffect(() => {
    // Function to ensure banner container has correct aspect ratio
    const fixBannerAspectRatio = () => {
      // Find all banner containers
      const bannerContainers = document.querySelectorAll('.banner-container');
      bannerContainers.forEach(container => {
        // Calculate height based on width to maintain 3:1 aspect ratio
        const containerWidth = container.offsetWidth;
        const bannerHeight = containerWidth / 3;
        
        // Apply the calculated height
        container.style.height = `${bannerHeight}px`;
        container.style.maxHeight = `${bannerHeight}px`;
        container.style.minHeight = `${bannerHeight}px`;
        container.style.boxSizing = 'border-box';
        container.style.overflow = 'hidden';
      });
    };
    
    // Run initially
    if (userData && hasPublished) {
      setTimeout(fixBannerAspectRatio, 100);
    }
    
    // Run on window resize
    window.addEventListener('resize', fixBannerAspectRatio);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', fixBannerAspectRatio);
    };
  }, [userData, currentTheme, hasPublished]);

  // Add this effect to your UserProfile.js file
  useEffect(() => {
    // Fix for mobile viewport height issues
    const setMobileHeight = () => {
      // First check if we're on mobile
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // Get the viewport height and multiply by 1% to get a value for a vh unit
        const vh = window.innerHeight * 0.01;
        // Set the value in the --vh custom property to the root of the document
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        
        // Find the profile container and ensure it has enough height
        const profileContainer = document.querySelector('.profileContainer') || 
                                document.querySelector('.profile-container');
        
        if (profileContainer) {
          // Make sure it's at least 100vh plus enough padding for the bottom links
          profileContainer.style.minHeight = `calc(100 * var(--vh) + 150px)`;
        }
      }
    };
    
    // Run on mount
    if (hasPublished) {
      setMobileHeight();
    
      // Also run on resize
      window.addEventListener('resize', setMobileHeight);
      
      // Handle orientation change specifically on mobile
      window.addEventListener('orientationchange', () => {
        // Slight delay to ensure new dimensions are available
        setTimeout(setMobileHeight, 100);
      });
      
      // Apply theme link color if available
      if (userData?.profile?.theme && userData?.profile?.themeSettings?.linkColor) {
        document.documentElement.style.setProperty('--theme-link-background', userData.profile.themeSettings.linkColor);
        console.log('Applied theme-specific link color:', userData.profile.themeSettings.linkColor);
      } else {
        // Reset to allow palette color to be used
        document.documentElement.style.setProperty('--theme-link-background', 'null');
      }
      
      return () => {
        window.removeEventListener('resize', setMobileHeight);
        window.removeEventListener('orientationchange', setMobileHeight);
      };
    }
  }, [userData, hasPublished]);

  const hashPassword = async (plaintext) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plaintext);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  // Track a failed attempt in the database - Array-based approach
  const trackFailedAttempt = async () => {
    if (!userData?.uid || !clientIp) return;
    
    try {
      const userRef = doc(db, "users", userData.uid);
      
      // Sanitize IP for Firestore field path
      const safeIp = clientIp.replace(/\./g, '_');
      
      // Add the current timestamp to the array for this IP
      await updateDoc(userRef, {
        [`profile.ghostMode.failedAttempts.${safeIp}`]: arrayUnion(Timestamp.now())
      });
      
      // Refresh the security data
      await checkGhostAttemptLimits(userData.uid, clientIp);
    } catch (err) {
      console.error("Error tracking failed attempt:", err);
    }
  };

  const handlePasswordSubmit = async (passwordInput) => {
    const ghostMode = userData?.profile?.ghostMode;
    if (!ghostMode || ghostMode.enabled !== true) {
      return { success: false };
    }
    
    // Don't allow attempts if locked out
    if (ghostSecurityData.isLocked) {
      return { 
        success: false, 
        error: "Too many attempts. Please try again later.",
        isLocked: true 
      };
    }

    try {
      const hashedInput = await hashPassword(passwordInput);
      
      // Check if password is correct
      if (
        typeof ghostMode.password === "object" &&
        ghostMode.password.hash &&
        hashedInput === ghostMode.password.hash
      ) {
        // Success! Password is correct
        setIsAuthenticated(true);
        return { success: true };
      }
      
      // Password was incorrect - track the attempt
      await trackFailedAttempt();
      
      // Return updated attempt info
      return { 
        success: false,
        remainingAttempts: ghostSecurityData.remainingAttempts - 1,
        isLocked: ghostSecurityData.remainingAttempts <= 1
      };
    } catch (error) {
      console.error("Error validating password:", error);
      return { success: false, error: "An error occurred." };
    }
  };

  // Updated handler for ghost link password submission
  const handleGhostLinkSubmit = async (linkId) => {
    const link = userData.profile.links.find((l) => l.id === linkId);
    const input = ghostLinkInputs[linkId] || "";
    
    if (!link || !link.ghostPassword) {
      setGhostLinkErrors((prev) => ({
        ...prev,
        [linkId]: "Invalid link or password",
      }));
      return;
    }
    
    if (input === link.ghostPassword) {
      setUnlockedLinks((prev) => ({ ...prev, [linkId]: true }));
      setGhostLinkErrors((prev) => ({ ...prev, [linkId]: "" }));
    } else {
      setGhostLinkErrors((prev) => ({
        ...prev,
        [linkId]: "Incorrect password",
      }));
    }
  };

  // Handle password input change
  const handlePasswordChange = (linkId, value) => {
    setGhostLinkInputs((prev) => ({
      ...prev,
      [linkId]: value,
    }));
    // Clear error when typing
    if (ghostLinkErrors[linkId]) {
      setGhostLinkErrors((prev) => ({
        ...prev,
        [linkId]: "",
      }));
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconObj = availableIcons.find((ic) => ic.name === iconName);
    return iconObj ? iconObj.component : null;
  };

  // Helper: compute the darkest color from the user's palette
  const getDarkestColor = () => {
    const colors = userData.profile?.palette?.colors;
    if (!colors || colors.length === 0) return "#000000";
    return colors.reduce((darkest, current) => {
      const hex = current.replace("#", "");
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      const darkestHex = darkest.replace("#", "");
      const dr = parseInt(darkestHex.slice(0, 2), 16) / 255;
      const dg = parseInt(darkestHex.slice(2, 4), 16) / 255;
      const db = parseInt(darkestHex.slice(4, 6), 16) / 255;
      const darkestLuminance = 0.2126 * dr + 0.7152 * dg + 0.0722 * db;
      return luminance < darkestLuminance ? current : darkest;
    }, colors[0]);
  };

  // Set link background color for CSS variables with theme support
  const setLinkBackgroundForCSS = useCallback(() => {
    // Only proceed if the profile has been published
    if (!hasPublished) return;

    // Check if there's a theme-specific link background color
    const computedStyle = getComputedStyle(document.documentElement);
    const themeBackgroundColor = computedStyle.getPropertyValue('--theme-link-background').trim();
    
    // If theme has specified a link background color, don't override it with palette color
    if (themeBackgroundColor !== 'null' && themeBackgroundColor !== '') {
      console.log('Using theme-specific link background color:', themeBackgroundColor);
      // The CSS variables in ThemeBase.css will handle the theme-specific color
    } 
    // Only set link background from palette if no theme color is specified
    else if (userData?.profile?.palette?.colors && userData.profile.palette.colors.length > 0) {
      document.documentElement.style.setProperty('--link-background', userData.profile.palette.colors[0]);
      console.log('Applied palette link background color:', userData.profile.palette.colors[0]);
    }
    
    // Update the link opacity variable if it exists in the user data
    if (userData?.profile?.palette?.linkOpacity !== undefined) {
      document.documentElement.style.setProperty('--link-background-opacity', userData.profile.palette.linkOpacity / 100);
    }
  }, [userData, hasPublished]);

  // Call this function when userData changes
  useEffect(() => {
    if (userData && hasPublished) {
      setLinkBackgroundForCSS();
    }
  }, [userData, setLinkBackgroundForCSS, hasPublished]);

  // Show loading spinner while data is being fetched
  if (loading) {
    return <div className={styles.loadingContainer}><div className={styles.loader}></div></div>;
  }

  // Show 404 if username doesn't exist
  if (!userExists) {
    return navigate("/404");
  }

  // Show the unpublished profile page if the user exists but hasn't published
  if (!hasPublished) {
    return (
      <>
        <Helmet>
          <title>{username} | Linkist.se</title>
          <meta property="og:title" content={`${username} | Linkist.se`} />
          <meta property="og:description" content="This profile hasn't been published yet" />
          <meta property="og:image" content="https://linkist.se/images/default-profile-preview.jpg" />
          <meta property="og:url" content={`https://linkist.se/${username}`} />
          <meta property="og:type" content="profile" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${username} | Linkist.se`} />
          <meta name="twitter:description" content="This profile hasn't been published yet" />
          <meta name="twitter:image" content="https://linkist.se/images/default-profile-preview.jpg" />
        </Helmet>
        <UnpublishedProfile username={username} />
      </>
    );
  }

  // At this point, we know the user exists and has published a profile
  const ghostMode = userData?.profile?.ghostMode;
  if (ghostMode?.enabled && !isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>{userData.profile?.displayName || username} | Linkist.se</title>
          <meta property="og:title" content={`${userData.profile?.displayName || username} | Linkist.se`} />
          <meta property="og:description" content={ghostMode.customMessage || "This profile is protected"} />
          <meta property="og:image" content="https://linkist.se/images/default-profile-preview.jpg" />
          <meta property="og:url" content={`https://linkist.se/${username}`} />
          <meta property="og:type" content="profile" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${userData.profile?.displayName || username} | Linkist.se`} />
          <meta name="twitter:description" content={ghostMode.customMessage || "This profile is protected"} />
          <meta name="twitter:image" content="https://linkist.se/images/default-profile-preview.jpg" />
        </Helmet>
        <GhostProfile 
          username={username} 
          onPasswordSubmit={handlePasswordSubmit} 
          customMessage={ghostMode.customMessage}
          securityData={ghostSecurityData}
          onAttemptTracking={trackFailedAttempt}
        />
      </>
    );
  }

  // Get palette colors
  const paletteColors = getPaletteColors(userData);

  // Assign palette colors to different elements
  const backgroundColor = paletteColors[4] || '#ffffff'; // Full page background
  const containerBackground = paletteColors[3] || '#f5f5f5'; // 60% container background
  const linkBackground = paletteColors[0] || '#000000'; // Link background - use first color

  // Determine if user is pro
  const isProUser = userData.tierType === "pro";
  // For free users, default icon color should be black
  const defaultIconColor = isProUser ? getDarkestColor() : "#000000";

  const bannerData = userData.profile?.banner;

  // Check if a banner image exists
  const hasBannerImage = bannerData?.url ? true : false;

  // Filter links to show only non-hidden ones
  const unsortedLinks = userData.profile?.links?.filter((link) => !link.hidden) || [];

  // Sort links with pinned items first, then by order
  const visibleLinks = [...unsortedLinks].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return a.order - b.order;
  });

  // Function to get text color with opacity
  const getTextColorWithOpacity = () => {
    // Default to a dark color with 70% opacity if nothing is set
    const defaultColor = '#444444';
    const defaultOpacity = 70;
    
    // Use the textColor from userData if available
    const textColor = userData?.profile?.textColor || defaultColor;
    
    // Check if the color already has an opacity component (8 digits with alpha)
    if (textColor && textColor.length === 9) {
      // Color already has opacity in format #RRGGBBAA
      return textColor;
    }
    
    // Use the stored opacity or default
    const opacity = userData?.profile?.textOpacity || defaultOpacity;
    
    // Convert opacity to hex
    const alphaHex = Math.round((opacity / 100) * 255).toString(16).padStart(2, '0');
    
    // Return the color with opacity
    return `${textColor}${alphaHex}`;
  };

  // Get text color with opacity
  const textColorWithOpacity = getTextColorWithOpacity();

  // Determine the best image to use for social sharing
  const getShareImage = () => {
    if (userData.photoURL) {
      return userData.photoURL;
    } else if (bannerData?.url) {
      return bannerData.url;
    } else {
      return "https://linkist.se/images/default-profile-preview.jpg";
    }
  };

  return (
    <>
      <Helmet>
        <title>{userData.profile?.displayName || username} | Linkist.se</title>
        <meta property="og:title" content={`${userData.profile?.displayName || username} | Linkist.se`} />
        <meta property="og:description" content={userData.profile?.bio || `Check out ${userData.profile?.displayName || username}'s links on Linkist.se`} />
        <meta property="og:image" content={getShareImage()} />
        <meta property="og:url" content={`https://linkist.se/${username}`} />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${userData.profile?.displayName || username} | Linkist.se`} />
        <meta name="twitter:description" content={userData.profile?.bio || `Check out ${userData.profile?.displayName || username}'s links on Linkist.se`} />
        <meta name="twitter:image" content={getShareImage()} />
      </Helmet>
      <div className={`${styles.profileContainer} theme-${currentTheme} profile-container`} style={{ backgroundColor }}>
        {/* Center container - 60% width with background color or image */}
        <div 
          className={`${styles.centerContainer} center-container ${hasBannerImage ? 'has-background-image' : ''}`} 
          style={{ backgroundColor: containerBackground }}
        >
          {/* Blurred background container */}
          <div className="blurred-content-container"></div>
          
          {bannerData?.url && (
            <div className="banner-container" style={{ aspectRatio: '3/1' }}>
              <img 
                src={bannerData.url} 
                alt="Banner"
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: `center ${bannerData.position}%`
                }}
              />
            </div>
          )}

          <div className="profile-content">
            <div className="avatar-container">
              {userData.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt="Profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div className="initials">
                  {(userData.profile?.displayName || username)[0].toUpperCase()}
                </div>
              )}
            </div>
            
            <h1 style={{ color: textColorWithOpacity }}>
              {userData.profile?.displayName || username}
            </h1>
            
            {/* Email and Phone with proper styling */}
            {(userData.profile?.email || userData.profile?.phone) && (
              <div className={styles.contactInfo}>
                {userData.profile?.email && (
                  <p className={`${styles.contactItem} email`} style={{ color: textColorWithOpacity }}>
                    <Mail size={16} style={{ marginRight: '8px' }} />
                    <span>{userData.profile.email}</span>
                  </p>
                )}
                
                {userData.profile?.phone && (
                  <p className={`${styles.contactItem} phone`} style={{ color: textColorWithOpacity }}>
                    <Phone size={16} style={{ marginRight: '8px' }} />
                    <span>{userData.profile.phone}</span>
                  </p>
                )}
              </div>
            )}
            
            {userData.profile?.bio && (
              <p className="bio" style={{ color: textColorWithOpacity }}>
                {userData.profile.bio}
              </p>
            )}

            {userData.profile?.icons && userData.profile.icons.length > 0 && (
              <div className="social-icons">
                {userData.profile.icons.map((iconData, i) => {
                  const IconComp = getIconComponent(iconData.component);
                  if (!IconComp) return null;
                  return (
                    <a
                      key={i}
                      className="icon"
                      href={iconData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: iconData.color || defaultIconColor
                      }}
                    >
                      <IconComp
                        size={24}
                        stroke={iconData.color || defaultIconColor}
                        color={iconData.color || defaultIconColor}
                      />
                    </a>
                  );
                })}
              </div>
            )}

            {visibleLinks.length > 0 && (
              <div className="links-container">
                {visibleLinks.map((link, i) => {
                  if (link.ghostProtected) {
                    // Use the GhostLinkButton component for ghost-protected links
                    return (
                      <GhostLinkButton
                        key={i}
                        link={link}
                        isUnlocked={unlockedLinks[link.id]}
                        themeClass={`theme-${currentTheme}`}
                        paletteColors={paletteColors}
                        passwordInput={ghostLinkInputs[link.id]}
                        onPasswordChange={handlePasswordChange}
                        onPasswordSubmit={handleGhostLinkSubmit}
                        error={ghostLinkErrors[link.id]}
                        textColor={textColorWithOpacity}
                        linkBackground={linkBackground}
                        // DO NOT SET opacity directly here
                      />
                    );
                  }
                  
                  // Regular non-ghost links - REMOVED opacity from inline styles
                  return (
                    <a
                      key={i}
                      className={`link-button ${link.imageUrl ? 'has-image' : ''}`}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        // REMOVED: backgroundColor: linkBackground,
                        color: textColorWithOpacity
                        // REMOVED: opacity: getLinkOpacity()
                        // CSS will handle the link background and opacity via variables
                      }}
                    >
                      {link.imageUrl && (
                        <div className="link-image-container">
                          <img 
                            src={link.imageUrl} 
                            alt="" 
                            className="link-thumbnail" 
                          />
                        </div>
                      )}
                      <span className="link-title">{link.title}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;